/* biome-ignore-all lint/style/useFilenamingConvention: TanStack Router requires $param syntax for dynamic routes */
import { Puck } from "@measured/puck";
import "@measured/puck/puck.css";
import { Button } from "@designs-by-maryam/ui/components/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { requireDesigner } from "@/functions/require-role";
import { type PuckPageData, puckConfig } from "@/lib/puck/config";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_dashboard/designer/editor/$pageId")({
	component: PageEditor,
	beforeLoad: async () => {
		await requireDesigner();
	},
});

const AUTO_SAVE_INTERVAL = 30_000; // 30 seconds

// Puck viewport configuration (built-in viewport toggle)
const PUCK_VIEWPORTS = [
	{ width: 375, height: "auto" as const, icon: "Smartphone", label: "Mobile" },
	{ width: 768, height: "auto" as const, icon: "Tablet", label: "Tablet" },
	{ width: 1280, height: "auto" as const, icon: "Monitor", label: "Desktop" },
];

function PageEditor() {
	const { pageId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Track current editor data for auto-save
	const currentDataRef = useRef<PuckPageData | null>(null);
	const hasUnsavedChangesRef = useRef(false);

	// Fetch page data for editing
	const pageQuery = useQuery(
		orpc.getPageForEdit.queryOptions({ input: { id: pageId } }),
	);

	// Mutation for saving page updates
	const updateMutation = useMutation({
		mutationFn: async (data: PuckPageData) =>
			orpc.updatePage.call({
				id: pageId,
				puck_data: data as unknown as {
					content: Array<{ type: string; props: Record<string, unknown> }>;
					root: { props?: Record<string, unknown> };
				},
			}),
		onSuccess: () => {
			// Invalidate page queries to refresh data
			queryClient.invalidateQueries({ queryKey: ["pages"] });
			hasUnsavedChangesRef.current = false;
			toast.success("Changes saved");
		},
		onError: (error) => {
			toast.error(`Failed to save: ${error.message}`);
		},
	});

	// Mutation for publishing page
	const publishMutation = useMutation({
		mutationFn: async () => orpc.publishPage.call({ id: pageId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pages"] });
			toast.success("Page published successfully");
		},
		onError: (error) => {
			toast.error(`Failed to publish: ${error.message}`);
		},
	});

	// Auto-save every 30 seconds if there are unsaved changes
	useEffect(() => {
		const intervalId = setInterval(() => {
			if (
				hasUnsavedChangesRef.current &&
				currentDataRef.current &&
				!updateMutation.isPending
			) {
				updateMutation.mutate(currentDataRef.current);
			}
		}, AUTO_SAVE_INTERVAL);

		return () => clearInterval(intervalId);
	}, [updateMutation]);

	// Track data changes from Puck
	const handleChange = useCallback((data: PuckPageData) => {
		currentDataRef.current = data;
		hasUnsavedChangesRef.current = true;
	}, []);

	// Handle publish from Puck editor
	const handlePublish = useCallback(
		async (data: PuckPageData) => {
			try {
				// First save the content
				await updateMutation.mutateAsync(data);
				// Then publish the page
				await publishMutation.mutateAsync();
			} catch {
				// Error handling done in mutation callbacks
			}
		},
		[updateMutation, publishMutation],
	);

	// Loading state
	if (pageQuery.isLoading) {
		return (
			<div className="flex h-screen items-center justify-center">
				<Loader2 className="h-8 w-8 animate-spin text-primary" />
				<span className="ml-2 text-muted-foreground">Loading page...</span>
			</div>
		);
	}

	// Error state
	if (pageQuery.error || !pageQuery.data) {
		return (
			<div className="flex h-screen flex-col items-center justify-center gap-4">
				<h1 className="font-bold text-2xl text-destructive">
					Failed to load page
				</h1>
				<p className="text-muted-foreground">
					{pageQuery.error?.message ?? "Page not found"}
				</p>
				<Button onClick={() => navigate({ to: "/designer" })} variant="outline">
					<ArrowLeft className="mr-2 h-4 w-4" />
					Back to Dashboard
				</Button>
			</div>
		);
	}

	const page = pageQuery.data;

	return (
		<div className="h-screen">
			{/* Header bar with save status */}
			<div className="absolute top-0 right-0 z-50 flex items-center gap-2 p-2">
				{updateMutation.isPending && (
					<span className="flex items-center gap-1 text-muted-foreground text-sm">
						<Loader2 className="h-4 w-4 animate-spin" />
						Saving...
					</span>
				)}
				{updateMutation.isSuccess && !updateMutation.isPending && (
					<span className="flex items-center gap-1 text-green-600 text-sm">
						<Save className="h-4 w-4" />
						Saved
					</span>
				)}
			</div>

			{/* Puck Editor with built-in viewport toggle */}
			<Puck
				config={puckConfig}
				data={page.puck_data as PuckPageData}
				headerPath={`/designer/editor/${pageId}`}
				headerTitle={page.title}
				onChange={handleChange}
				onPublish={handlePublish}
				viewports={PUCK_VIEWPORTS}
			/>
		</div>
	);
}
